import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { quotesAPI, fixedRoutesAPI, contractsAPI, usersAPI, quoteSettingsAPI, serviceTypesAPI, vehicleTypesAPI } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import RoutePriceCalculator from "../components/RoutePriceCalculator";
import ClientSelector from "../components/ClientSelector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Search,
  FileText,
  MapPin,
  Clock,
  DollarSign,
  ArrowRight,
  Plus,
  Eye,
  Receipt,
  Sparkles,
  Loader2,
  Calculator,
  User,
  AlertCircle,
  Lock,
  Download,
  X,
  Pencil
} from "lucide-react";
import { cn } from "../lib/utils";

const VAT_RATE = 0.16; // 16% IVA México

const ON_BEHALF_REASONS = [
  { value: "NO_ACCESS", label: "Vendedor sin acceso al sistema" },
  { value: "OPERATIONAL_SUPPORT", label: "Apoyo operativo" },
  { value: "URGENT_CAPTURE", label: "Captura urgente" },
  { value: "SHIFT_COVERAGE", label: "Cobertura de turno" },
  { value: "OTHER", label: "Otro" }
];

export default function Cotizaciones() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [fixedRoutes, setFixedRoutes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  // Quote mode: "QUICK" (minimal fields) or "STANDARD" (full fields)
  const [quoteMode, setQuoteMode] = useState("STANDARD");
  // PDF Generation
  const [pdfLoading, setPdfLoading] = useState(null);
  // Bulk selection for ADMIN
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    quote_mode: "STANDARD",
    base_price: 0,
    extras: [],
    distance_km: null,
    duration_min: null,
    notes: "",
    full_itinerary: "",
    requires_invoice: false,
    vat_rate: VAT_RATE,
    suggested_price: null,
    pricing_method: null,
    pricing_rules_snapshot: null,
    route_source: null,
    // Multi-currency fields
    currency: "MXN",
    exchange_rate: null,
    // Sales owner fields
    sales_owner_id: "",
    on_behalf_reason: "",
    on_behalf_note: "",
    // Vendedor Logistica reassignment reason (when changing vendor)
    vl_reassign_reason: "",
    // P1-C: Sales agent field preserved in data but not used in UI flow
    // sales_agent_id: "", // DEPRECATED - not used in active flow
    // CRM Client (v4.12.0)
    client_id: null,
    // Client fields (legacy - kept for backward compatibility)
    client_name: "",
    client_phone: "",
    client_email: "",
    origin: "",
    destination: "",
    origin_lat: null,
    origin_lng: null,
    dest_lat: null,
    dest_lng: null,
    pickup_datetime: "",
    end_datetime: "",  // P0-2: End date/time required
    service_type: "transfer",
    transfer_trip_type: "SENCILLO", // For TRANSFER: SENCILLO | REDONDO
    passengers: 1,
    // Vehicle lines
    vehicle_lines: []
  });
  const [newExtra, setNewExtra] = useState({ name: "", amount: 0 });
  const [newVehicleLine, setNewVehicleLine] = useState({ type: "", quantity: 1, capacity: 4, base_price: 0 });
  const [vehicleTypes, setVehicleTypes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Helper to check user role capabilities for salesperson assignment
  const isVendedorOnly = user?.role === "vendedor";
  const isVendedorLogistica = user?.role === "vendedor_logistica";
  const isAdminRole = user?.role === "admin";
  const isAdminVendedor = user?.role === "admin_vendedor";
  const isLogisticaOnly = user?.role === "logistica";
  
  // admin_vendedor has admin permissions but behaves like admin for UI (can select vendor, no auto-assign)
  const hasAdminPermissions = isAdminRole || isAdminVendedor;
  
  // Solo vendedor puro se auto-asigna y queda bloqueado
  // vendedor_logistica, admin, logistica seleccionan manualmente
  const isAutoAssignRole = isVendedorOnly;
  
  // Determine if vendor selection is required (mandatory for non-vendedor roles)
  const vendorSelectionRequired = hasAdminPermissions || isLogisticaOnly || isVendedorLogistica;
  
  // These roles can change the vendor selection
  const canChangeVendor = hasAdminPermissions || isLogisticaOnly || isVendedorLogistica;
  
  // Set default sales_owner_id when user loads (auto-assign for vendedor roles only)
  useEffect(() => {
    if (user && !formData.sales_owner_id) {
      // Auto-assign only for vendedor puro
      // admin_vendedor and vendedor_logistica do NOT auto-assign
      if (isAutoAssignRole) {
        setFormData(prev => ({ ...prev, sales_owner_id: user.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAutoAssignRole]);

  // P1-C DEPRECATED: sales_agent_id auto-resolve removed
  // sales_owner_id is now the single source of truth for commissions

  // Set default vehicle type when types are loaded
  useEffect(() => {
    if (vehicleTypes.length > 0 && !newVehicleLine.type) {
      const defaultType = vehicleTypes[0];
      setNewVehicleLine(prev => ({
        ...prev,
        type: defaultType.code,
        capacity: defaultType.capacity || 4
      }));
    }
  }, [vehicleTypes]);

  const fetchData = async () => {
    try {
      const [quotesRes, routesRes, vendedoresRes, serviceTypesRes, vehicleTypesRes] = await Promise.all([
        quotesAPI.getAll(),
        fixedRoutesAPI.getAll(),
        usersAPI.getVendedores(),
        serviceTypesAPI.getAll(),
        vehicleTypesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setQuotes(quotesRes.data);
      setFixedRoutes(routesRes.data);
      setVendedores(vendedoresRes.data);
      setServiceTypes(serviceTypesRes.data);
      
      // Filter only active vehicle types
      const activeVehicleTypes = (vehicleTypesRes.data || []).filter(t => t.is_active !== false);
      setVehicleTypes(activeVehicleTypes.length > 0 ? activeVehicleTypes : [
        { code: "sedan", name: "Sedán", capacity: 4 },
        { code: "suv", name: "SUV", capacity: 6 },
        { code: "van", name: "Van", capacity: 14 },
        { code: "sprinter", name: "Sprinter", capacity: 20 }
      ]);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  // Check if creating on behalf of another user
  const isOnBehalf = formData.sales_owner_id && user && formData.sales_owner_id !== user.id;
  
  // Check if vendedor_logistica is reassigning to another vendor (requires reason)
  const isVLReassigning = isVendedorLogistica && isOnBehalf;

  // Handle price suggestion applied from RoutePriceCalculator
  const handleRoutePriceApplied = (suggestion) => {
    // Apply distance multiplier for TRANSFER REDONDO
    let multiplier = 1;
    if (formData.service_type === "transfer" && formData.transfer_trip_type === "REDONDO") {
      multiplier = 2;
    }
    
    const effectivePrice = suggestion.basePrice * multiplier;
    
    setFormData(prev => ({
      ...prev,
      base_price: effectivePrice,
      suggested_price: suggestion.suggestedPrice,
      pricing_method: suggestion.pricingMethod,
      pricing_rules_snapshot: suggestion.rulesSnapshot,
      route_source: suggestion.routeSource,
      distance_km: (suggestion.distanceKm || 0) * multiplier || prev.distance_km,
      duration_min: (suggestion.durationMin || 0) * multiplier || prev.duration_min
    }));
  };
  
  // Helper: Get vehicle type label - flags ambiguous legacy codes for review
  const getVehicleTypeLabel = (typeCode) => {
    if (!typeCode) return "Sin tipo";
    const exactMatch = vehicleTypes.find(vt => vt.code === typeCode);
    if (exactMatch) return exactMatch.name;
    
    // Check for ambiguous legacy code
    const possibleMatches = vehicleTypes.filter(vt => 
      vt.code.startsWith(typeCode + "_") || vt.code.startsWith(typeCode.toLowerCase() + "_")
    );
    if (possibleMatches.length > 0) {
      const baseName = typeCode.charAt(0).toUpperCase() + typeCode.slice(1).toLowerCase();
      return `${baseName} (revisar capacidad)`;
    }
    
    const fallbackLabels = {
      sedan: "Sedán", suv: "SUV", van: "Van", sprinter: "Sprinter",
      suburban: "Suburban", autobus: "Autobús (revisar capacidad)", 
      bus: "Autobús (revisar capacidad)", limusina: "Limusina"
    };
    return fallbackLabels[typeCode.toLowerCase()] || typeCode;
  };

  // Handle adding vehicle line - NEW: Expand quantity to individual entries with price
  const handleAddVehicleLine = () => {
    if (newVehicleLine.quantity > 0 && newVehicleLine.type) {
      // Expand quantity to individual entries
      const newLines = [];
      const basePrice = newVehicleLine.base_price || 0;
      
      for (let i = 0; i < newVehicleLine.quantity; i++) {
        newLines.push({
          id: `${Date.now()}-${i}`,
          type: newVehicleLine.type,
          capacity: newVehicleLine.capacity,
          base_price: basePrice,
          notes: ""
        });
      }
      
      setFormData(prev => ({
        ...prev,
        vehicle_lines: [...prev.vehicle_lines, ...newLines]
      }));
      
      // Reset to first available type
      const defaultType = vehicleTypes[0] || { code: "sedan", capacity: 4 };
      setNewVehicleLine({ type: defaultType.code, quantity: 1, capacity: defaultType.capacity || 4, base_price: 0 });
    }
  };

  const handleRemoveVehicleLine = (id) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.filter(v => v.id !== id)
    }));
  };
  
  // NEW: Update individual vehicle line price
  const handleUpdateVehicleLinePrice = (id, newPrice) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.map(v => 
        v.id === id ? { ...v, base_price: parseFloat(newPrice) || 0 } : v
      )
    }));
  };
  
  // NEW: Calculate totals from vehicle lines
  const calculateTotalsFromLines = () => {
    const vehicleLinesTotal = formData.vehicle_lines.reduce((sum, v) => sum + (v.base_price || 0), 0);
    const extrasTotal = formData.extras.reduce((sum, e) => sum + e.amount, 0);
    const subtotal = vehicleLinesTotal + extrasTotal;
    const vatAmount = formData.requires_invoice ? subtotal * formData.vat_rate : 0;
    const total = subtotal + vatAmount;
    
    return { vehicleLinesTotal, extrasTotal, subtotal, vatAmount, total };
  };
  
  // Auto-update base_price when vehicle_lines change
  useEffect(() => {
    if (formData.vehicle_lines.length > 0) {
      const totals = calculateTotalsFromLines();
      setFormData(prev => ({
        ...prev,
        base_price: totals.vehicleLinesTotal
      }));
    }
  }, [formData.vehicle_lines]);

  const handleAddExtra = () => {
    if (newExtra.name && newExtra.amount > 0) {
      setFormData(prev => ({
        ...prev,
        extras: [...prev.extras, { ...newExtra }]
      }));
      setNewExtra({ name: "", amount: 0 });
    }
  };

  const handleRemoveExtra = (index) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  // ADMIN: Toggle quote selection
  const toggleQuoteSelection = (quoteId) => {
    setSelectedQuotes(prev => 
      prev.includes(quoteId) 
        ? prev.filter(id => id !== quoteId)
        : [...prev, quoteId]
    );
  };

  // ADMIN: Bulk delete selected quotes
  const handleBulkDelete = async () => {
    if (selectedQuotes.length === 0) {
      toast.error("No hay cotizaciones seleccionadas");
      return;
    }
    
    if (!window.confirm(`¿Eliminar ${selectedQuotes.length} cotizaciones seleccionadas? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    setBulkDeleteLoading(true);
    try {
      const res = await quotesAPI.bulkDelete(selectedQuotes);
      toast.success(res.data.message);
      setSelectedQuotes([]);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar cotizaciones");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Set quote_mode in formData
    const submitData = { ...formData, quote_mode: quoteMode };
    
    // VALIDATION: Vendor is required for admin and logistica-only users
    if (vendorSelectionRequired && !submitData.sales_owner_id) {
      toast.error("Debe seleccionar un vendedor asignado");
      return;
    }
    
    // sales_agent_id validation removed - sales_owner_id is the single source
    
    // Validate on_behalf_reason if creating for another user
    if (isOnBehalf && !submitData.on_behalf_reason) {
      toast.error("Debe seleccionar una razón al capturar para otro vendedor");
      return;
    }
    
    // Validate vl_reassign_reason if vendedor_logistica is reassigning
    if (isVLReassigning && !submitData.vl_reassign_reason) {
      toast.error("Debe especificar el motivo de reasignación del vendedor");
      return;
    }
    
    // Validate exchange rate for USD
    if (submitData.currency === "USD" && (!submitData.exchange_rate || submitData.exchange_rate <= 0)) {
      toast.error("Debe especificar un tipo de cambio válido para cotizaciones en USD");
      return;
    }
    
    // Always required: origin, destination, date, service_type
    if (!submitData.origin || !submitData.destination || !submitData.pickup_datetime) {
      toast.error("Complete origen, destino y fecha de inicio");
      return;
    }
    // P0: Validate end_datetime is after pickup_datetime
    if (!submitData.end_datetime) {
      toast.error("Debe especificar fecha y hora de finalización");
      return;
    }
    if (new Date(submitData.end_datetime) <= new Date(submitData.pickup_datetime)) {
      toast.error("La fecha y hora de finalización debe ser posterior a la fecha y hora de inicio");
      return;
    }
    
    // Validate based on quote mode
    if (quoteMode === "STANDARD") {
      // STANDARD: client required (either CRM client_id or legacy name+phone)
      if (!submitData.client_id && (!submitData.client_name || !submitData.client_phone)) {
        toast.error("Debe seleccionar un cliente para cotización STANDARD");
        return;
      }
      if (!submitData.vehicle_lines || submitData.vehicle_lines.length === 0) {
        toast.error("Debe seleccionar al menos un tipo de unidad");
        return;
      }
    }
    // QUICK mode: client fields are optional
    
    try {
      if (editingQuote) {
        await quotesAPI.update(editingQuote.id, submitData);
        toast.success("Cotización actualizada");
      } else {
        await quotesAPI.create(submitData);
        toast.success("Cotización creada");
      }
      setDialogOpen(false);
      setEditingQuote(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingQuote ? "Error al actualizar cotización" : "Error al crear cotización"));
    }
  };

  const handleConvertToContract = async (quote) => {
    // Check if quote is QUICK - cannot convert directly
    if (quote.quote_mode === "QUICK") {
      toast.error("No se puede crear contrato desde cotización QUICK. Primero conviértala a STANDARD.");
      return;
    }
    
    try {
      const contractData = {
        quote_id: quote.id,
        client_name: quote.client_name || "Cliente",
        client_phone: quote.client_phone || "",
        client_email: quote.client_email || "",
        corporate_id: quote.corporate_id,
        origin: quote.origin || "",
        destination: quote.destination || "",
        origin_lat: quote.origin_lat,
        origin_lng: quote.origin_lng,
        dest_lat: quote.dest_lat,
        dest_lng: quote.dest_lng,
        pickup_datetime: quote.pickup_datetime || new Date().toISOString(),
        end_datetime: quote.end_datetime,
        service_type: quote.service_type || "transfer",
        transfer_trip_type: quote.transfer_trip_type || "SENCILLO",
        passengers: quote.passengers || 1,
        luggage: quote.luggage || 0,
        flight_number: quote.flight_number,
        base_price: quote.base_price,
        extras: quote.extras || [],
        total_price: quote.total,
        requires_invoice: quote.requires_invoice || false,
        vat_rate: quote.vat_rate || VAT_RATE,
        subtotal_amount: quote.subtotal_amount,
        vat_amount: quote.vat_amount,
        vehicle_lines: quote.vehicle_lines || [],
        // Multi-currency
        currency: quote.currency || "MXN",
        exchange_rate: quote.exchange_rate,
        // HERENCIA: Vendedor desde cotización (P0 - Lógica de Asignación de Vendedor)
        sales_owner_id: quote.sales_owner_id || user?.id,
        // P1-C: Carry sales_agent_id from quote to contract
        sales_agent_id: quote.sales_agent_id || null
      };

      const response = await contractsAPI.create(contractData);
      toast.success("Contrato creado exitosamente");
      navigate(`/contratos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear contrato");
    }
  };

  const resetForm = () => {
    setPriceSuggestion(null);
    setQuoteMode("STANDARD");
    // Auto-assign for vendedor roles on reset
    const initialSalesOwnerId = isAutoAssignRole ? user?.id : "";
    setFormData({
      quote_mode: "STANDARD",
      base_price: 0,
      extras: [],
      distance_km: null,
      duration_min: null,
      notes: "",
    full_itinerary: "",
      requires_invoice: false,
      vat_rate: VAT_RATE,
      suggested_price: null,
      pricing_method: null,
      pricing_rules_snapshot: null,
      route_source: null,
      currency: "MXN",
      exchange_rate: null,
      sales_owner_id: initialSalesOwnerId,
      on_behalf_reason: "",
      on_behalf_note: "",
      vl_reassign_reason: "",
      client_id: null,
      client_name: "",
      client_phone: "",
      client_email: "",
      origin: "",
      destination: "",
      origin_lat: null,
      origin_lng: null,
      dest_lat: null,
      dest_lng: null,
      pickup_datetime: "",
      end_datetime: "",
      service_type: "transfer",
      transfer_trip_type: "SENCILLO",
      passengers: 1,
      vehicle_lines: []
    });
    setNewExtra({ name: "", amount: 0 });
    const defaultType = vehicleTypes[0] || { code: "sedan", capacity: 4 };
    setNewVehicleLine({ type: defaultType.code, quantity: 1, capacity: defaultType.capacity || 4 });
  };

  // Edit quote: populate form with existing data
  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setQuoteMode(quote.quote_mode || "STANDARD");
    setFormData({
      quote_mode: quote.quote_mode || "STANDARD",
      base_price: quote.base_price || 0,
      extras: quote.extras || [],
      distance_km: quote.distance_km || null,
      duration_min: quote.duration_min || null,
      notes: quote.notes || "",
      full_itinerary: quote.full_itinerary || "",
      requires_invoice: quote.requires_invoice || false,
      vat_rate: quote.vat_rate || VAT_RATE,
      suggested_price: quote.suggested_price || null,
      pricing_method: quote.pricing_method || null,
      pricing_rules_snapshot: quote.pricing_rules_snapshot || null,
      route_source: quote.route_source || null,
      currency: quote.currency || "MXN",
      exchange_rate: quote.exchange_rate || null,
      sales_owner_id: quote.sales_owner_id || "",
      on_behalf_reason: quote.on_behalf_reason || "",
      on_behalf_note: quote.on_behalf_note || "",
      vl_reassign_reason: quote.vl_reassign_reason || "",
      client_id: quote.client_id || null,
      client_name: quote.client_name || "",
      client_phone: quote.client_phone || "",
      client_email: quote.client_email || "",
      origin: quote.origin || "",
      destination: quote.destination || "",
      origin_lat: quote.origin_lat || null,
      origin_lng: quote.origin_lng || null,
      dest_lat: quote.dest_lat || null,
      dest_lng: quote.dest_lng || null,
      pickup_datetime: quote.pickup_datetime || "",
      end_datetime: quote.end_datetime || "",
      service_type: quote.service_type || "transfer",
      transfer_trip_type: quote.transfer_trip_type || "SENCILLO",
      passengers: quote.passengers || 1,
      vehicle_lines: quote.vehicle_lines || []
    });
    setDialogOpen(true);
  };


  // Generate PDF
  const handleGeneratePDF = async (quoteId) => {
    setPdfLoading(quoteId);
    try {
      const response = await quotesAPI.generatePDF(quoteId);
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success("PDF generado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar PDF");
    } finally {
      setPdfLoading(null);
    }
  };

  // Calculate pricing
  const subtotalPrice = formData.base_price + formData.extras.reduce((sum, e) => sum + e.amount, 0);
  const vatAmount = formData.requires_invoice ? subtotalPrice * VAT_RATE : 0;
  const totalPrice = subtotalPrice + vatAmount;
  
  // Calculate MXN equivalents for USD
  const exchangeRate = formData.exchange_rate || 1;
  const subtotalMXN = formData.currency === "USD" ? subtotalPrice * exchangeRate : subtotalPrice;
  const vatMXN = formData.currency === "USD" ? vatAmount * exchangeRate : vatAmount;
  const totalMXN = formData.currency === "USD" ? totalPrice * exchangeRate : totalPrice;

  const filteredQuotes = quotes.filter(q => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        q.client_name?.toLowerCase().includes(search) ||
        q.client_phone?.includes(search) ||
        q.origin?.toLowerCase().includes(search) ||
        q.destination?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="cotizaciones-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Cotizaciones</h1>
            <p className="text-slate-500 mt-1">Genera cotizaciones para clientes</p>
          </div>
          <Button onClick={() => { resetForm(); setEditingQuote(null); setDialogOpen(true); }} className="gap-2" data-testid="new-quote-btn">
            <Plus className="w-4 h-4" />
            Nueva Cotización
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar cotizaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* ADMIN: Bulk Actions Bar */}
        {isAdmin && selectedQuotes.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-amber-800 font-medium">
                {selectedQuotes.length} cotización(es) seleccionada(s)
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedQuotes([])}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  Deseleccionar
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  data-testid="bulk-delete-quotes-btn"
                >
                  {bulkDeleteLoading ? "Eliminando..." : `Eliminar ${selectedQuotes.length}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ADMIN: Select All Option */}
        {isAdmin && filteredQuotes.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={selectedQuotes.length === filteredQuotes.length && filteredQuotes.length > 0}
              onChange={() => {
                if (selectedQuotes.length === filteredQuotes.length) {
                  setSelectedQuotes([]);
                } else {
                  setSelectedQuotes(filteredQuotes.map(q => q.id));
                }
              }}
              className="w-4 h-4 rounded border-slate-300"
            />
            <span>Seleccionar todas ({filteredQuotes.length})</span>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : filteredQuotes.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay cotizaciones</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && <TableHead className="w-12"></TableHead>}
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Moneda</TableHead>
                    <TableHead>Subtotal</TableHead>
                    <TableHead>IVA</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Factura</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotes.map((quote) => {
                    const currencySymbol = quote.currency === "USD" ? "USD $" : "$";
                    return (
                    <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`} className={selectedQuotes.includes(quote.id) ? "bg-amber-50" : ""}>
                      {isAdmin && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedQuotes.includes(quote.id)}
                            onChange={() => toggleQuoteSelection(quote.id)}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {new Date(quote.created_at).toLocaleDateString("es-MX")}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{quote.client_name || "—"}</div>
                        <div className="text-xs text-slate-500">{quote.origin?.slice(0, 20)}...</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={quote.currency === "USD" ? "outline" : "secondary"} className="text-xs">
                          {quote.currency || "MXN"}
                        </Badge>
                        {quote.currency === "USD" && quote.exchange_rate && (
                          <div className="text-xs text-slate-500 mt-1">TC: {quote.exchange_rate}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {currencySymbol}{(quote.subtotal_amount || quote.base_price + (quote.extras_total || 0))?.toLocaleString("es-MX")}
                      </TableCell>
                      <TableCell>
                        {quote.requires_invoice ? (
                          <span className="text-blue-600">
                            {currencySymbol}{quote.vat_amount?.toLocaleString("es-MX")}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          {currencySymbol}{quote.total?.toLocaleString("es-MX")}
                        </div>
                        {quote.currency === "USD" && quote.total_amount_mxn && (
                          <div className="text-xs text-slate-500">
                            MXN ${quote.total_amount_mxn?.toLocaleString("es-MX")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {quote.requires_invoice ? (
                          <Badge className="bg-blue-100 text-blue-800">
                            <Receipt className="w-3 h-3 mr-1" />
                            Sí
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">No</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          quote.status === "contrato_generado" && "bg-green-100 text-green-800",
                          quote.status === "cotizada" && "bg-blue-100 text-blue-800",
                          quote.status === "pendiente_cotizar" && "bg-yellow-100 text-yellow-800"
                        )}>
                          {quote.status?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* PDF Button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleGeneratePDF(quote.id)}
                                  disabled={pdfLoading === quote.id}
                                  data-testid={`pdf-quote-${quote.id}`}
                                >
                                  {pdfLoading === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Generar PDF</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Convert to Contract Button */}
                          {quote.status !== "contrato_generado" && (
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditQuote(quote)}
                              data-testid={`edit-quote-${quote.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                          )}
                          {quote.status !== "contrato_generado" && (
                            <Button 
                              size="sm" 
                              onClick={() => handleConvertToContract(quote)}
                              data-testid={`convert-quote-${quote.id}`}
                            >
                              <ArrowRight className="w-4 h-4 mr-1" />
                              Contrato
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingQuote(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingQuote ? "Editar Cotización" : "Nueva Cotización"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Toggle Quote Mode: QUICK vs STANDARD */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <div>
                      <Label className="text-sm font-medium">Cotización Rápida</Label>
                      <p className="text-xs text-slate-500">Sin datos de cliente obligatorios</p>
                    </div>
                  </div>
                  <Switch
                    checked={quoteMode === "QUICK"}
                    onCheckedChange={(checked) => {
                      setQuoteMode(checked ? "QUICK" : "STANDARD");
                    }}
                    data-testid="quick-quote-toggle"
                  />
                </div>
                {quoteMode === "QUICK" && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Cotización rápida – No garantiza seguimiento ni vinculación a cliente.</span>
                  </div>
                )}
              </div>

              {/* Client Info - Required for STANDARD, optional for QUICK */}
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-600" />
                  <h3 className="font-medium">Datos del Cliente</h3>
                  {quoteMode === "QUICK" && (
                    <Badge variant="outline" className="text-xs">Opcional</Badge>
                  )}
                </div>
                
                {/* CRM Client Selector for STANDARD quotes */}
                {quoteMode === "STANDARD" ? (
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <ClientSelector
                      value={formData.client_id}
                      onChange={(clientId, clientData) => {
                        if (clientData) {
                          setFormData(prev => ({
                            ...prev,
                            client_id: clientId,
                            client_name: clientData.name || clientData.legal_name,
                            client_phone: clientData.phone || "",
                            client_email: clientData.email || ""
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            client_id: null,
                            client_name: "",
                            client_phone: "",
                            client_email: ""
                          }));
                        }
                      }}
                      required={true}
                      placeholder="Buscar o crear cliente..."
                    />
                  </div>
                ) : (
                  /* QUICK mode - simple text fields (optional) */
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          value={formData.client_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                          placeholder="Nombre del cliente"
                          data-testid="client-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input
                          value={formData.client_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                          placeholder="10 dígitos"
                          data-testid="client-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email (opcional)</Label>
                      <Input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                        placeholder="correo@ejemplo.com"
                        data-testid="client-email-input"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Service Info - Always required */}
              <div className="space-y-4 p-4 border rounded-lg">
                <h3 className="font-medium">Información del Servicio</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Origen *</Label>
                    <Input
                      value={formData.origin}
                      onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                      placeholder="Dirección de origen"
                      data-testid="origin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Destino *</Label>
                    <Input
                      value={formData.destination}
                      onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                      placeholder="Dirección de destino"
                      data-testid="destination-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha y Hora de Inicio *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.pickup_datetime}
                      onChange={(e) => setFormData(prev => ({ ...prev, pickup_datetime: e.target.value }))}
                      data-testid="pickup-datetime-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha y Hora de Fin *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.end_datetime}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_datetime: e.target.value }))}
                      min={formData.pickup_datetime}
                      data-testid="end-datetime-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Servicio *</Label>
                    <Select 
                      value={formData.service_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                    >
                      <SelectTrigger data-testid="service-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.length > 0 ? (
                          serviceTypes.map(st => (
                            <SelectItem key={st.code} value={st.code}>{st.name}</SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="transfer">Transfer</SelectItem>
                            <SelectItem value="aeropuerto">Aeropuerto</SelectItem>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="disposicion">Disposición</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Transfer Trip Type: SENCILLO | REDONDO */}
                {formData.service_type === "transfer" && (
                  <div className="space-y-2">
                    <Label>Tipo de Transfer</Label>
                    <Select 
                      value={formData.transfer_trip_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, transfer_trip_type: value }))}
                    >
                      <SelectTrigger data-testid="transfer-trip-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SENCILLO">Sencillo (solo ida)</SelectItem>
                        <SelectItem value="REDONDO">Redondo (ida y vuelta)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.transfer_trip_type === "REDONDO" && (
                      <p className="text-xs text-blue-600">* El precio se duplicará automáticamente</p>
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle Lines */}
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Tipo(s) de Unidad</h3>
                  {quoteMode === "STANDARD" && (
                    <Badge variant="outline" className="text-xs">Obligatorio</Badge>
                  )}
                </div>
                
                {/* Add vehicle line */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <Select 
                      value={newVehicleLine.type} 
                      onValueChange={(value) => {
                        const selectedType = vehicleTypes.find(t => t.code === value);
                        setNewVehicleLine(prev => ({ 
                          ...prev, 
                          type: value, 
                          capacity: selectedType?.capacity || 4 
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="vehicle-type-select">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleTypes.map(vt => (
                          <SelectItem key={vt.code} value={vt.code}>
                            {vt.name} ({vt.capacity || 4} pax)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-20 space-y-1">
                    <Label className="text-xs">Cant.</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newVehicleLine.quantity}
                      onChange={(e) => setNewVehicleLine(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      data-testid="vehicle-quantity-input"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">Precio c/u</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="0"
                      value={newVehicleLine.base_price || ""}
                      onChange={(e) => setNewVehicleLine(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                      data-testid="vehicle-price-input"
                    />
                  </div>
                  <Button type="button" onClick={handleAddVehicleLine} size="sm" disabled={!newVehicleLine.type} className="mt-5">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Vehicle lines list - NEW: Individual entries with editable price */}
                {formData.vehicle_lines.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 font-medium mb-2">
                      Unidades ({formData.vehicle_lines.length}):
                    </p>
                    {formData.vehicle_lines.map((v, i) => {
                      const typeName = getVehicleTypeLabel(v.type);
                      return (
                        <div key={v.id || i} className="flex items-center gap-2 p-2 bg-white rounded border">
                          <span className="text-sm flex-1">
                            {i + 1}. {typeName} ({v.capacity} pax)
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={v.base_price || 0}
                              onChange={(e) => handleUpdateVehicleLinePrice(v.id, e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveVehicleLine(v.id)} className="h-8 w-8 p-0">
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      );
                    })}
                    <div className="flex justify-end pt-2 border-t">
                      <span className="text-sm font-medium">
                        Total unidades: ${formData.vehicle_lines.reduce((sum, v) => sum + (v.base_price || 0), 0).toLocaleString("es-MX")}
                      </span>
                    </div>
                  </div>
                )}
                
                {formData.vehicle_lines.length === 0 && quoteMode === "STANDARD" && (
                  <p className="text-xs text-amber-600">* Agregue al menos un tipo de unidad</p>
                )}
              </div>

              {/* Route Price Calculator */}
              <RoutePriceCalculator
                origin={formData.origin}
                destination={formData.destination}
                originLat={formData.origin_lat}
                originLng={formData.origin_lng}
                destLat={formData.dest_lat}
                destLng={formData.dest_lng}
                vehicleType={formData.vehicle_lines[0]?.type || "sedan"}
                passengers={formData.passengers}
                serviceType={formData.service_type}
                currency={formData.currency}
                exchangeRate={formData.exchange_rate}
                requiresInvoice={formData.requires_invoice}
                salesOwnerId={formData.sales_owner_id}
                onApplySuggestion={handleRoutePriceApplied}
              />

              {/* Sales Owner Selection (Role-based Logic - P0) */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-2 flex-wrap">
                  <User className="w-5 h-5 text-slate-600" />
                  <Label className="font-medium">
                    Vendedor Asignado {(vendorSelectionRequired || isAutoAssignRole) && <span className="text-red-500">*</span>}
                  </Label>
                  {/* Role-based badges */}
                  {isVendedorOnly && formData.sales_owner_id === user?.id && (
                    <Badge className="bg-green-100 text-green-800 text-xs">Auto-asignado (fijo)</Badge>
                  )}
                  {isVendedorLogistica && !formData.sales_owner_id && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">Selección obligatoria</Badge>
                  )}
                  {vendorSelectionRequired && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">Obligatorio</Badge>
                  )}
                </div>
                
                {/* Contextual help messages */}
                {vendorSelectionRequired && !formData.sales_owner_id && (
                  <p className="text-sm text-amber-600">
                    Como {hasAdminPermissions ? "administrador" : "logística"}, debe seleccionar un vendedor
                  </p>
                )}
                {isVendedorLogistica && !formData.sales_owner_id && (
                  <p className="text-sm text-amber-600">
                    Seleccione el vendedor asignado a esta cotización
                  </p>
                )}
                {isVendedorOnly && (
                  <p className="text-sm text-green-600">
                    Usted será el vendedor asignado automáticamente
                  </p>
                )}
                
                <Select 
                  value={formData.sales_owner_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, sales_owner_id: value }))}
                  disabled={isVendedorOnly} // vendedor puro no puede cambiar
                >
                  <SelectTrigger 
                    data-testid="sales-owner-select" 
                    className={cn(
                      !formData.sales_owner_id && vendorSelectionRequired ? "border-amber-400" : "",
                      isVendedorOnly ? "bg-slate-100 cursor-not-allowed" : ""
                    )}
                  >
                    <SelectValue placeholder="Seleccionar vendedor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendedores.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} {v.id === user?.id ? "(Yo)" : ""} {v.role === "admin" ? "(Admin)" : v.role === "admin_vendedor" ? "(Admin+V)" : v.role === "vendedor_logistica" ? "(V+L)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* P1-C: Sales Agent selector removed - sales_owner_id is now the single source of truth */}

                {/* Vendedor Logistica reassignment reason - required when changing vendor */}
                {isVLReassigning && (
                  <div className="space-y-3 pt-3 border-t border-blue-200 bg-blue-50 p-3 rounded-md">
                    <div className="flex items-center gap-2 text-blue-700">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Reasignación de vendedor</span>
                      <Badge className="bg-blue-200 text-blue-800 text-xs">Obligatorio</Badge>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Motivo de reasignación *</Label>
                      <Textarea
                        value={formData.vl_reassign_reason}
                        onChange={(e) => setFormData(prev => ({ ...prev, vl_reassign_reason: e.target.value }))}
                        placeholder="Explique por qué reasigna esta cotización a otro vendedor..."
                        rows={2}
                        className={cn(
                          "resize-none",
                          !formData.vl_reassign_reason ? "border-blue-400" : ""
                        )}
                        data-testid="vl-reassign-reason"
                        required
                      />
                      <p className="text-xs text-blue-600">
                        Este motivo será registrado en el historial de auditoría para control de comisiones
                      </p>
                    </div>
                  </div>
                )}

                {/* On-behalf fields - shown when creating for another user */}
                {isOnBehalf && (
                  <div className="space-y-3 pt-3 border-t">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Captura en nombre de otro vendedor</span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Razón *</Label>
                      <Select
                        value={formData.on_behalf_reason}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, on_behalf_reason: value }))}
                      >
                        <SelectTrigger data-testid="on-behalf-reason-select">
                          <SelectValue placeholder="Seleccionar razón..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ON_BEHALF_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Nota adicional (opcional)</Label>
                      <Textarea
                        value={formData.on_behalf_note}
                        onChange={(e) => setFormData(prev => ({ ...prev, on_behalf_note: e.target.value }))}
                        placeholder="Detalles adicionales..."
                        rows={2}
                        data-testid="on-behalf-note"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="base_price">Precio Base *</Label>
                  <div className="relative">
                    <Input
                      id="base_price"
                      type="number"
                      step="any"
                      value={formData.base_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                      required
                      data-testid="base-price-input"
                      className={cn(
                        formData.suggested_price && formData.base_price !== formData.suggested_price 
                          ? "border-amber-400" 
                          : ""
                      )}
                    />
                    {formData.suggested_price && formData.base_price !== formData.suggested_price && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Diferente al sugerido (${formData.suggested_price.toLocaleString("es-MX")})
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="duration">Duración estimada de ruta (min)</Label>
                    <span 
                      className="text-xs text-slate-400 cursor-help" 
                      title="Estimación de Google Maps. El bloqueo real del vehículo depende de Fecha Inicio/Fin."
                    >
                      ⓘ
                    </span>
                  </div>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration_min || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_min: parseInt(e.target.value) }))}
                    data-testid="duration-input"
                  />
                </div>
                {/* Tiempo reservado calculado */}
                {formData.pickup_datetime && formData.end_datetime && (
                  <div className="space-y-2">
                    <Label className="text-slate-500">Tiempo reservado</Label>
                    <div className="p-2 bg-slate-50 rounded border text-sm font-medium text-slate-700">
                      {(() => {
                        const start = new Date(formData.pickup_datetime);
                        const end = new Date(formData.end_datetime);
                        const diffMs = end - start;
                        if (diffMs <= 0) return "—";
                        const hours = Math.floor(diffMs / (1000 * 60 * 60));
                        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        if (hours > 0) return `${hours}h ${mins}min`;
                        return `${mins} min`;
                      })()}
                    </div>
                    <p className="text-xs text-slate-400">Periodo de bloqueo del vehículo</p>
                  </div>
                )}
              </div>

              {/* Extras */}
              <div className="space-y-4">
                <Label>Extras</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Concepto"
                    value={newExtra.name}
                    onChange={(e) => setNewExtra(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1"
                    data-testid="extra-name-input"
                  />
                  <Input
                    type="number"
                    step="any"
                    placeholder="Monto"
                    value={newExtra.amount || ""}
                    onChange={(e) => setNewExtra(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="w-32"
                    data-testid="extra-amount-input"
                  />
                  <Button type="button" onClick={handleAddExtra} data-testid="add-extra-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {formData.extras.length > 0 && (
                  <div className="space-y-2">
                    {formData.extras.map((extra, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span>{extra.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">${extra.amount.toLocaleString("es-MX")}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveExtra(index)}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Toggle */}
              <div className="space-y-2">
                <Label>Itinerario detallado (aparece en el PDF)</Label>
                <Textarea
                  value={formData.full_itinerary}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_itinerary: e.target.value }))}
                  placeholder={"Describa el itinerario paso a paso...\nEj: 10:00 - Recoger en aeropuerto\n10:30 - Traslado a hotel\n11:00 - Check-in"}
                  rows={4}
                  className="resize-none"
                  data-testid="full-itinerary-input"
                />
                <p className="text-xs text-slate-500">Cada linea sera un paso del itinerario en la cotizacion PDF</p>
              </div>

              {/* Invoice Toggle - original */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  <div>
                    <Label htmlFor="requires_invoice" className="font-medium text-blue-900">
                      ¿Requiere Factura?
                    </Label>
                    <p className="text-sm text-blue-600">
                      {formData.requires_invoice 
                        ? "Se aplicará IVA del 16% al total" 
                        : "No se agregará IVA a la cotización"}
                    </p>
                  </div>
                </div>
                <Switch
                  id="requires_invoice"
                  checked={formData.requires_invoice}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_invoice: checked }))}
                  data-testid="requires-invoice-switch"
                />
              </div>

              {/* Currency Selection */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
                <div className="space-y-2">
                  <Label>Moneda</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      currency: value,
                      exchange_rate: value === "MXN" ? null : prev.exchange_rate 
                    }))}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">🇲🇽 MXN - Peso Mexicano</SelectItem>
                      <SelectItem value="USD">🇺🇸 USD - Dólar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.currency === "USD" && (
                  <div className="space-y-2">
                    <Label htmlFor="exchange_rate">Tipo de Cambio *</Label>
                    <Input
                      id="exchange_rate"
                      type="number"
                      step="any"
                      min="1"
                      placeholder="Ej: 17.50"
                      value={formData.exchange_rate || ""}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        exchange_rate: parseFloat(e.target.value) || null 
                      }))}
                      required={formData.currency === "USD"}
                      data-testid="exchange-rate-input"
                    />
                    <p className="text-xs text-slate-500">1 USD = ? MXN</p>
                  </div>
                )}
              </div>

              {/* Pricing Summary */}
              <div className={cn(
                "p-4 rounded-lg space-y-2",
                formData.requires_invoice ? "bg-blue-50" : "bg-green-50"
              )}>
                <div className="flex justify-between items-center text-sm">
                  <span>Subtotal</span>
                  <span className="font-medium">
                    {formData.currency === "USD" ? "USD " : ""}${subtotalPrice.toLocaleString("es-MX")}
                  </span>
                </div>
                
                {formData.requires_invoice && (
                  <div className="flex justify-between items-center text-sm text-blue-700">
                    <span>IVA (16%)</span>
                    <span className="font-medium">
                      {formData.currency === "USD" ? "USD " : ""}${vatAmount.toLocaleString("es-MX")}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-lg font-medium">Total</span>
                  <span className={cn(
                    "text-2xl font-bold",
                    formData.requires_invoice ? "text-blue-600" : "text-green-600"
                  )}>
                    {formData.currency === "USD" ? "USD " : ""}${totalPrice.toLocaleString("es-MX")}
                  </span>
                </div>
                
                {/* MXN Equivalent for USD */}
                {formData.currency === "USD" && formData.exchange_rate && (
                  <div className="pt-2 border-t border-slate-200 text-slate-600">
                    <div className="flex justify-between items-center text-sm">
                      <span>Equivalente en MXN</span>
                      <span className="font-medium">${totalMXN.toLocaleString("es-MX")} MXN</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">TC: {formData.exchange_rate} MXN/USD</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-quote-btn"
                >
                  {editingQuote ? "Guardar Cambios" : "Crear Cotización"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
